import { CHARTJS_MAX_TICKS_LIMIT, formatShortMonthDay } from '@/components/charts/generic';
import type { MetricChartSpec } from '@/components/charts/generic/types';
import type { HealthTrendPoint } from '@/domain/health';
import {
    formatPercentageByLocale,
    formatUniversalUnitByLocale,
    type Locale,
} from '@/utils/localeFormatters';

import { createHealthDevelopmentMeasurementLines } from '../_utils/healthDevelopmentTooltip';
import { getHealthDevelopmentYAxisConfig } from '../_utils/healthDevelopmentYAxis';

export interface HealthDevelopmentDataPoint {
    day: string;
    value: number | null;
}

export interface HealthDevelopmentDataset {
    label: string;
    color: string;
    data: HealthDevelopmentDataPoint[];
}

export type HealthDevelopmentLineChartDataset = HealthDevelopmentDataset;

export interface HealthDevelopmentChartSpecParams {
    datasets: HealthDevelopmentDataset[];
    locale: Locale;
    /** The parameter's raw trend: scales the y axis and gives the measurement count. */
    parameterTrend: HealthTrendPoint[];
    /** The `healthModule` translation function, for the measurement count line. */
    t?: (key: 'detail.common.measurements') => string;
}

/** Most date labels: the cards are narrow, and the first and last day are always labelled. */
const MAX_X_TICKS = 7;
/** Gentler than the shared smoothing, so a sparse score does not overshoot between days. */
const LINE_TENSION = 0.2;
/** Stands in for the leading space the swatch and score were once parted by. */
const SWATCH_GAP = 4;

/**
 * The score development lines as a MetricChart spec: one percent line per score without point
 * markers, on the fixed axis `getHealthDevelopmentYAxisConfig` picks, with the number of fish
 * measured on the hovered day in the bold tooltip footer. A score with no value that day is left
 * out of the tooltip. The chart sits in small cards and does not zoom.
 */
export function buildHealthDevelopmentChartSpec(
    params: HealthDevelopmentChartSpecParams
): MetricChartSpec<'line'> {
    const { datasets, locale, parameterTrend, t } = params;
    const days = [
        ...new Set(datasets.flatMap((dataset) => dataset.data.map((point) => point.day))),
    ].sort((a, b) => a.localeCompare(b));
    const yAxis = getHealthDevelopmentYAxisConfig(parameterTrend);

    return {
        kind: 'line',
        days,
        series: datasets.map((dataset, index) => ({
            key: `score:${index}`,
            label: dataset.label,
            color: dataset.color,
            showInLegend: false,
            marker: { radius: 0, hoverRadius: 0 },
            tension: LINE_TENSION,
            data: dataset.data,
        })),
        xTicks: {
            maxTicksLimit: MAX_X_TICKS,
            keepEnds: true,
            format: (day) => formatShortMonthDay(day, locale),
        },
        left: {
            label: '',
            min: yAxis.min,
            max: yAxis.max,
            stepSize: yAxis.stepSize,
            maxTicksLimit: CHARTJS_MAX_TICKS_LIMIT,
            autoSkip: false,
            format: (value, target) =>
                target === 'tick'
                    ? formatUniversalUnitByLocale(value, '%', locale, yAxis.decimals)
                    : formatPercentageByLocale(value, locale, yAxis.decimals),
        },
        locale,
        zoom: false,
        tooltipSkipEmpty: true,
        tooltipBoxPadding: SWATCH_GAP,
        ...(t && parameterTrend.length > 0
            ? {
                  tooltipFooterLines: createHealthDevelopmentMeasurementLines(
                      t,
                      locale,
                      parameterTrend,
                      days
                  ),
              }
            : {}),
    };
}
